import { useMemo } from 'react';
import { useGetMyCompanyQuery } from '../redux/company';
import { Company } from '../types/company';

export interface CompanyAccessFallback {
  transport?: boolean;
  massTransport?: boolean;
  dieselTransport?: boolean;
  rental?: boolean;
  workersPlanner?: boolean;
  machinePlanner?: boolean;
  weekPlanner?: boolean;
  map?: boolean;
}

interface CompanyAccess {
  canAccessTransport: boolean;
  canAccessMassTransport: boolean;
  canAccessDieselTransport: boolean;
  canAccessRental: boolean;
  canAccessWorkersPlanner: boolean;
  canAccessMachinePlanner: boolean;
  canAccessWeekPlanner: boolean;
  canAccessMap: boolean;
}

export const resolveCompanyAccess = (
  company: Company | undefined,
  fallback: CompanyAccessFallback = {},
): CompanyAccess => ({
  canAccessTransport: company?.transportationPlanner ?? fallback.transport ?? false,
  canAccessMassTransport: company?.massTransportationPlanner ?? fallback.massTransport ?? false,
  canAccessDieselTransport: company?.dieselTransportationPlanner ?? fallback.dieselTransport ?? false,
  canAccessRental: company?.rental ?? fallback.rental ?? false,
  canAccessWorkersPlanner: company?.workersPlanner ?? fallback.workersPlanner ?? false,
  canAccessMachinePlanner: company?.machinePlanner ?? fallback.machinePlanner ?? false,
  canAccessWeekPlanner: company?.weekPlanner ?? fallback.weekPlanner ?? false,
  canAccessMap: company?.map ?? fallback.map ?? false,
});

// Generic hook factory - core logic extracted
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useCompanyAccessWithQuery = (
  queryHook: () => any,
  fallback: CompanyAccessFallback = {},
) => {
  const {
    transport = false,
    massTransport = false,
    dieselTransport = false,
    rental = false,
    workersPlanner = false,
    machinePlanner = false,
    weekPlanner = false,
    map = false,
  } = fallback;

  const { data: company, isLoading: isCompanyLoading, isFetching: isCompanyFetching } = queryHook();

  const access = useMemo(() => resolveCompanyAccess(company, {
    transport,
    massTransport,
    dieselTransport,
    rental,
    workersPlanner,
    machinePlanner,
    weekPlanner,
    map,
  }), [
    company,
    transport,
    massTransport,
    dieselTransport,
    rental,
    workersPlanner,
    machinePlanner,
    weekPlanner,
    map,
  ]);

  const isCompanyAccessLoading = !company && (isCompanyLoading || isCompanyFetching);

  return {
    company,
    access,
    isCompanyAccessLoading,
  };
};

// Backward compatible default hook using rental API
export const useCompanyAccess = (fallback: CompanyAccessFallback = {}) => useCompanyAccessWithQuery(() => useGetMyCompanyQuery(), fallback);
